import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;

export default function useRecordLock(recordId, recordType = 'accountingClient') {
  const { isAuthReady, isAuthenticated, isRefreshRevoked } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [lockedByName, setLockedByName] = useState('');
  const [lockedAt, setLockedAt] = useState(null);

  const ownerRef = useRef(false);

  const releaseLock = useCallback(async () => {
    if (!recordId || !ownerRef.current || !isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    try {
      await api.delete(`/locks/${recordId}`);
    } catch (error) {
      // Ignore release errors during navigation/unmount.
    } finally {
      ownerRef.current = false;
      setIsOwner(false);
    }
  }, [recordId, isAuthReady, isAuthenticated, isRefreshRevoked]);

  useEffect(() => {
    let isMounted = true;

    const acquireLock = async () => {
      if (!recordId || !isAuthReady || !isAuthenticated || isRefreshRevoked) {
        return;
      }

      try {
        const response = await api.post(`/locks/${recordId}`, { recordType });

        if (!isMounted) {
          return;
        }

        if (response?.status === 200 && response?.data?.locked === false) {
          ownerRef.current = true;
          setIsOwner(true);
          setIsLocked(false);
          setLockedByName('');
          setLockedAt(null);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error?.response?.status === 423 && error?.response?.data?.locked) {
          ownerRef.current = false;
          setIsOwner(false);
          setIsLocked(true);
          setLockedByName(error.response.data.lockedByName || 'another user');
          setLockedAt(error.response.data.lockedAt || null);
        }
      }
    };

    acquireLock();

    return () => {
      isMounted = false;
      if (ownerRef.current) {
        releaseLock();
      }
    };
  }, [recordId, recordType, isAuthReady, isAuthenticated, isRefreshRevoked, releaseLock]);

  useEffect(() => {
    if (!recordId || !isOwner || !isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        await api.patch(`/locks/${recordId}`);
      } catch (error) {
        // Ignore heartbeat errors; lock state will be reconciled by server responses.
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [recordId, isOwner, isAuthReady, isAuthenticated, isRefreshRevoked]);

  return {
    isLocked,
    isOwner,
    lockedByName,
    lockedAt,
    releaseLock
  };
}
